import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { DEBOUNCE_DELAY } from 'utils/constants/app/magic-numbers-app';
import {
  CUSTOM_CATEGORIES,
  CUSTOM_CATEGORY_IDS_WITHOUT_PROVIDERS
} from 'utils/constants/casino/custom-categories';
import { ECasinoQueryKey } from 'utils/constants/store/query-keys';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { showToastError } from 'utils/generic/show-toast-error';
import {
  NewCasinoGamesConfigs,
  NewCasinoGamesFetchingParams
} from 'interfaces/new-casino';
import {
  getCasinoGames,
  getPromotedCasinoGames
} from 'services/casino/casino-fetch-games-query';
import { useSelectedCategory } from './useSelectedCategory';
import { useDebounce } from 'hooks/useDebounce';
import { setEnhancedParams } from 'store/actions/new-casino';
import {
  getCanRequestCategoriesNestedData,
  getCanRequestProvidersNestedData
} from 'store/selectors/new-casino';
import { getInitialFetchParams } from '../constants/fetch-params';

type UseGamesQueryProps = {
  configs: Partial<NewCasinoGamesConfigs>;
  params: Partial<NewCasinoGamesFetchingParams>;
  isFetchDisabled?: boolean;
};

const MAX_CASINO_GAMES_QUERY_KEY_LENGTH = 3;

export const useGamesQuery = ({
  configs,
  params,
  isFetchDisabled: isFetchDisabledFromOuterScope
}: UseGamesQueryProps) => {
  const categoriesFetched = useSelector(getCanRequestCategoriesNestedData);
  const providersFetched = useSelector(getCanRequestProvidersNestedData);
  const { selectedCategory } = useSelectedCategory({ configs });
  const dispatch = useDispatch();
  // ATTENTION: this array is also used for getting cache in useGamesCombinedData
  const [casinoGamesQueryKey, setCasinoGamesQueryKey] = useState(() => [
    ECasinoQueryKey.CasinoGames as string,
    configs.moduleId
  ]);

  const enhancedParams: Partial<NewCasinoGamesFetchingParams> = {
    ...getInitialFetchParams(configs),
    ...params
  };

  const paramsWithoutPagination = {
    ...enhancedParams,
    offset: undefined,
    limit: undefined
  };

  const offset = enhancedParams.offset || 0;

  const fetchEnabledBinding = useMemo(() => {
    // check possibility for request data from CMS in case of binding with other elements to keep requests' right order

    const isCategoryWithoutProvider =
      CUSTOM_CATEGORY_IDS_WITHOUT_PROVIDERS.includes(selectedCategory);

    // when bound to "categories" and "providers" elements
    if (configs.bindWithCategories && configs.bindWithProviders) {
      return (
        categoriesFetched && (providersFetched || isCategoryWithoutProvider)
      );
    }

    //when bound to "categories" element only
    if (configs.bindWithCategories) {
      return categoriesFetched;
    }

    //when bound to "providers" element only
    if (configs.bindWithProviders) {
      return providersFetched || isCategoryWithoutProvider;
    }

    //when not bound to any element
    return true;
  }, [
    configs.bindWithCategories,
    configs.bindWithProviders,
    categoriesFetched,
    providersFetched,
    selectedCategory
  ]);

  const fetchEnabledByQueryKey = useMemo(() => {
    if (configs.skipQueryKeysCheck) {
      return true;
    }

    // if query keys are less than 3, it means that there is no fetch for the data and query is decided to be a miss
    if (casinoGamesQueryKey.length < MAX_CASINO_GAMES_QUERY_KEY_LENGTH) {
      return false;
    }

    if (
      [CUSTOM_CATEGORIES.special.id, CUSTOM_CATEGORIES.specialLive.id].includes(
        selectedCategory
      )
    ) {
      return true;
    }

    const parsedParams = JSON.parse(casinoGamesQueryKey[2] as string);

    // check for parsedParams length is because react query has an in-between case where it has only one parameter
    // in that case there is no fetch for the data and query is decided to be a miss
    return Object.keys(parsedParams).length >= 2;
  }, [casinoGamesQueryKey, selectedCategory]);

  const fetchEnabledByPagination = Boolean(
    enhancedParams.offset !== undefined || enhancedParams.limit !== undefined
  );

  const isFetchEnabled =
    !SpringConfigs.MOCKED_DATA &&
    !isFetchDisabledFromOuterScope &&
    fetchEnabledBinding &&
    fetchEnabledByQueryKey &&
    fetchEnabledByPagination;

  const paramsWithoutPaginationStringified = JSON.stringify(
    paramsWithoutPagination,
    null
  );

  // ATTENTION: setCasinoGamesQueryKey is also used for getting cache in useGamesCombinedData. Modify it carefully
  const { isDebouncing } = useDebounce(
    () => {
      setCasinoGamesQueryKey([
        ECasinoQueryKey.CasinoGames,
        configs.moduleId,
        paramsWithoutPaginationStringified,
        offset
      ]);
    },
    DEBOUNCE_DELAY._10,
    [paramsWithoutPaginationStringified, offset, configs.moduleId]
  );

  const isPromotedCategory = [
    CUSTOM_CATEGORIES.special.id,
    CUSTOM_CATEGORIES.specialLive.id
  ].includes(selectedCategory);

  const getGames = isPromotedCategory ? getPromotedCasinoGames : getCasinoGames;

  const { data, error, isLoading } = useQuery({
    queryKey: casinoGamesQueryKey,
    queryFn: () => getGames(enhancedParams),
    retry: false,
    enabled: isFetchEnabled
  });

  useEffect(() => {
    dispatch(setEnhancedParams(enhancedParams));
  }, [JSON.stringify(enhancedParams)]);

  useEffect(() => {
    if (!error) {
      return;
    }

    showToastError(String(error));
  }, [error]);

  return {
    params,
    data,
    error,
    isLoading,
    isFetchEnabled,
    isDebouncing,
    paramsWithoutPaginationStringified
  };
};
